<template>
  <div class="material-list">
    <div class="header">
      <h3>Materials</h3>
      <button @click="navigateToCreateMaterial" class="new-material-button">
        New Material
      </button>
    </div>

    <div class="search-filter-row">
      <input
          type="text"
          v-model="searchQuery"
          placeholder="Search by Name or Batch..."
          class="search-input"
      />
      <button @click="showFilters = !showFilters" class="toggle-filter-button">
        {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
      </button>
    </div>

    <!-- Collapsible Filter Menu -->
    <div v-if="showFilters" class="filters">
      <!-- Simple batch filter (contains); adjust to your needs -->
      <input
          type="text"
          v-model="batchFilter"
          class="text-input"
          placeholder="Filter batch (contains)"
      />
    </div>

    <table class="material-table">
      <thead>
      <tr>
        <th @click="sortMaterials('name')">
          Name
          <i
              v-if="currentSortColumn === 'name'"
              class="filter-arrow"
              :class="setArrowDirection(currentSortDirection)"
          ></i>
        </th>
        <th @click="sortMaterials('batch')">
          Batch
          <i
              v-if="currentSortColumn === 'batch'"
              class="filter-arrow"
              :class="setArrowDirection(currentSortDirection)"
          ></i>
        </th>
      </tr>
      </thead>
      <tbody>
      <tr
          v-for="material in paginatedMaterials"
          :key="material.id"
          @click="navigateToMaterialDetail(material.id)"
          class="material-row"
      >
        <td>{{ material.name }}</td>
        <td>{{ material.batch }}</td>
      </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button
          v-for="page in visiblePageNumbers"
          :key="page"
          @click="setPage(page)"
          :class="{ active: page === currentPage }"
      >
        {{ page }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_ENDPOINTS } from '@/services/config.js';
import { mapState } from 'vuex';

export default {
  name: 'MaterialList',
  data() {
    return {
      materials: [],
      searchQuery: '',
      batchFilter: '',
      currentPage: 1,
      materialsPerPage: 15,
      currentSortColumn: 'name',
      currentSortDirection: 'asc',
      showFilters: false,
      sortedMaterials: [],
    };
  },
  created() {
    this.fetchMaterials();
  },
  methods: {
    navigateToCreateMaterial() {
      this.$router.push({ path: '/create-material' });
    },
    async fetchMaterials() {
      try {
        const response = await axios.get(API_ENDPOINTS.materials, {
          params: { entra_id: this.loggedInUser.entra_id },
        });
        this.materials = response.data || [];
        this.sortedMaterials = [...this.materials];
        this.sortMaterials(this.currentSortColumn);
      } catch (error) {
        console.error('Error fetching materials:', error);
      }
    },
    sortMaterials(column) {
      if (this.currentSortColumn === column) {
        this.currentSortDirection =
            this.currentSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSortDirection = 'asc';
        this.currentSortColumn = column;
      }
      const dir = this.currentSortDirection === 'asc' ? 1 : -1;

      this.sortedMaterials.sort((a, b) => {
        const av = (a?.[column] ?? '').toString().toLowerCase();
        const bv = (b?.[column] ?? '').toString().toLowerCase();
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
      // keep current page within bounds if list size changed
      if (this.currentPage > this.totalPages) this.currentPage = this.totalPages || 1;
    },
    setPage(pageNumber) {
      this.currentPage = pageNumber;
    },
    setArrowDirection(sortDirection) {
      return sortDirection === 'desc' ? 'bi bi-arrow-down' : 'bi bi-arrow-up';
    },
    navigateToMaterialDetail(materialId) {
      this.$router.push({ name: 'MaterialDetailPage', params: { id: materialId } });
    },
  },
  computed: {
    ...mapState(['loggedInUser']),
    filteredMaterials() {
      const q = this.searchQuery.toLowerCase();
      const batchQ = this.batchFilter.toLowerCase();
      return this.sortedMaterials.filter((m) => {
        const name = (m.name || '').toLowerCase();
        const batch = (m.batch || '').toLowerCase();

        const matchesSearch =
            !q || name.includes(q) || batch.includes(q);
        const matchesBatch = !batchQ || batch.includes(batchQ);

        return matchesSearch && matchesBatch;
      });
    },
    paginatedMaterials() {
      const start = (this.currentPage - 1) * this.materialsPerPage;
      const end = start + this.materialsPerPage;
      return this.filteredMaterials.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredMaterials.length / this.materialsPerPage) || 1;
    },
    visiblePageNumbers() {
      const range = 15;
      let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
      let endPage = startPage + range - 1;
      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - range + 1);
      }
      return Array.from(
          { length: endPage - startPage + 1 },
          (_, i) => startPage + i
      );
    },
  },
  watch: {
    // Reset to page 1 when filters/search change so users see results immediately
    searchQuery() {
      this.currentPage = 1;
    },
    batchFilter() {
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.material-list {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      color: $primary-color;
    }

    .new-material-button {
      padding: 8px 16px;
      background-color: $primary-color;
      color: #fff;
      border: none;
      border-radius: 5px;
      cursor: pointer;
    }

    .new-material-button:hover {
      background-color: darken($primary-color, 10%);
    }
  }

  .search-filter-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
  }

  .search-input {
    flex: 1;
    margin: 0;
  }

  .toggle-filter-button {
    width: auto;
    margin: 0;
    padding: 6px 10px;
    white-space: nowrap;
  }

  .material-table {
    width: 100%;
  }

  .material-table th {
    background-color: $form-color;
    border: 1px solid $primary-color;
    cursor: pointer;
  }

  .filters {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    width: 100%;
    max-width: none;
    margin: 6px 0;

    .text-input {
      flex: 1;
    }
  }

  .material-row {
    background-color: $secondary-color;
    cursor: pointer;
  }

  .material-row:hover {
    background-color: $form-color;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  .pagination button {
    margin: 0 5px;
    padding: 5px 10px;
    background-color: $primary-color;
    border: 1px solid $secondary-color;
    border-radius: 5px;
    cursor: pointer;
  }

  .pagination button:hover,
  .pagination button.active {
    background-color: darken($primary-color, 10%);
  }
}
</style>
